import { useNavigate } from 'react-router-dom';
import { useEffect, useRef } from 'react';
import gsap from 'gsap';

export default function Hero() {
  const navigate = useNavigate();
  const shimmerLine1 = useRef<HTMLSpanElement>(null);
  const shimmerLine2 = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    if (!shimmerLine1.current || !shimmerLine2.current) return;

    const tl = gsap.timeline({ repeat: -1, repeatDelay: 4 });

    tl.to([shimmerLine1.current, shimmerLine2.current], {
      backgroundPosition: '300% center',
      duration: 3.2,
      ease: 'power2.inOut',
    });

    tl.set([shimmerLine1.current, shimmerLine2.current], {
      backgroundPosition: '-300% center',
    });
  }, []);

  const handleTreatmentsClick = () => {
    navigate('/addiction-services');
  };

  const handleLocationClick = () => {
    navigate('/location');
  };

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-center overflow-hidden bg-background-100">
      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 text-center w-full pt-28 sm:pt-32 pb-8 sm:pb-12">
        {/* Overline */}
        <p className="text-[10px] sm:text-xs tracking-[0.25em] uppercase text-foreground-700 mb-4 sm:mb-5 font-['Roboto_Condensed',sans-serif]">
          Addiction Treatment & Pharmacy
        </p>

        {/* Headline */}
        <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl text-foreground-950 mb-4 sm:mb-5 leading-[1.1] font-['Playfair_Display',serif] font-normal">
          <span
            ref={shimmerLine1}
            className="inline-block"
            style={{
              backgroundImage: 'linear-gradient(90deg, oklch(var(--foreground-950)) 0%, oklch(var(--foreground-950)) 30%, rgba(255,255,255,0.65) 50%, oklch(var(--foreground-950)) 70%, oklch(var(--foreground-950)) 100%)',
              backgroundSize: '300% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Your Journey to
          </span>{' '}
          <span
            ref={shimmerLine2}
            className="inline-block italic"
            style={{
              backgroundImage: 'linear-gradient(90deg, oklch(var(--primary-600)) 0%, oklch(var(--primary-600)) 30%, rgba(255,255,255,0.65) 50%, oklch(var(--primary-600)) 70%, oklch(var(--primary-600)) 100%)',
              backgroundSize: '300% 100%',
              backgroundClip: 'text',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            recovery
          </span>
        </h1>

        {/* Subtitle */}
        <p className="text-sm sm:text-base text-foreground-700 mb-8 sm:mb-10 leading-relaxed max-w-2xl mx-auto font-['Roboto_Condensed',sans-serif] font-light">
          Compassionate addiction treatment and comprehensive pharmacy services in Thornhill, Ontario. We&apos;re here to support your path to wellness.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
          <button
            onClick={handleTreatmentsClick}
            className="w-full sm:w-auto group bg-primary-500 hover:bg-primary-600 text-background-50 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-medium transition-all hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
          >
            Find Treatments
            <span className="w-5 h-5 rounded-full bg-primary-400 flex items-center justify-center group-hover:bg-primary-300 transition-colors">
              <i className="ri-arrow-right-line text-background-50 text-xs"></i>
            </span>
          </button>
          <button
            onClick={handleLocationClick}
            className="w-full sm:w-auto border border-foreground-200 bg-background-50/60 text-foreground-900 px-6 sm:px-8 py-3 sm:py-3.5 rounded-full text-sm sm:text-base font-medium hover:bg-background-50 hover:border-foreground-300 transition-all hover:scale-105 whitespace-nowrap cursor-pointer inline-flex items-center justify-center gap-2"
          >
            <i className="ri-map-pin-line text-sm"></i>
            Location & Hours
          </button>
        </div>
      </div>

      {/* Pharmacy Illustration */}
      <div className="relative z-10 mt-2 sm:mt-4 flex-shrink-0 px-4">
        <img
          src="https://static.readdy.ai/image/621fe86c950ded9745dd2c19cd7c5595/49bb97e1a55bd1ba04d895a7d1483454.png"
          alt="Friendly pharmacist illustration"
          className="w-80 h-80 sm:w-64 sm:h-64 md:w-80 md:h-80 object-contain"
        />
      </div>
    </section>
  );
}